import { FunctionsHttpError } from "@supabase/supabase-js";

import { logError } from "../../utils/error";
import { supabase } from "../supabase/client";

const INVITE_USER_ERROR = "Failed to invite user";
const DELETE_USER_ERROR = "Failed to delete user";

const DISPLAYABLE_ERROR_DETAILS = [
  "User with this email already exists",
  "No Spoke user found for this email",
  "More than one Spoke user found for this email",
  "User is not associated with this account"
];

interface FunctionErrorBody {
  error?: unknown;
  details?: unknown;
}

interface FunctionErrorMessageParams {
  error: Error;
  fallbackMessage: string;
}

const readFunctionErrorBody = async (error: Error): Promise<FunctionErrorBody> => {
  if (!(error instanceof FunctionsHttpError)) {
    return {};
  }

  try {
    const body: FunctionErrorBody | null = await error.context.json();
    return body ?? {};
  } catch (_bodyError) {
    return {};
  }
};

const isDisplayableDetail = (details: unknown): details is string =>
  typeof details === "string" && DISPLAYABLE_ERROR_DETAILS.includes(details);

const genFunctionErrorMessage = async (params: FunctionErrorMessageParams): Promise<string> => {
  const { error, fallbackMessage } = params;
  const { error: message, details } = await readFunctionErrorBody(error);

  if (typeof message !== "string" || !message) {
    return fallbackMessage;
  }

  return isDisplayableDetail(details) ? `${message}: ${details}` : message;
};

export type AccountRole = "user" | "admin";

export interface AccountUser {
  id: string;
  email: string;
  role: AccountRole;
  spokeTenantId: string;
  created: string;
  modified: string;
  deleted: string | null;
  analysisGroups: string[] | null;
}

/**
 * Fetch users associated with the current tenant
 */
export const fetchUsers = async (tenantId: string): Promise<AccountUser[]> => {
  try {
    const { data, error } = await supabase
      .from("v_account_user")
      .select(`
        id,
        role,
        spoke_tenant_id,
        email,
        created,
        modified,
        analysis_groups
      `)
      .eq("spoke_tenant_id", tenantId)
      .order("email", { ascending: true });

    if (error) {
      logError("Error fetching users", error);
      throw error;
    }

    // v_account_user is a view so every column types as nullable, but rows
    // filtered by spoke_tenant_id always have these core fields populated.
    const users: AccountUser[] = data.map((user) => ({
      id: user.id!,
      spokeTenantId: user.spoke_tenant_id!,
      email: user.email!,
      role: user.role!,
      created: user.created!,
      modified: user.modified!,
      deleted: null,
      analysisGroups: user.analysis_groups as string[] | null
    }));

    return users;
  } catch (error) {
    logError("Error in fetchUsers", error);
    throw error;
  }
};

/**
 * Invite a new user to the current tenant by calling the invite-user edge function
 */
export const inviteUser = async (
  email: string,
  role: AccountRole,
  tenantId: string
): Promise<void> => {
  try {
    const response = await supabase.functions.invoke("invite-user", {
      body: {
        email,
        role,
        tenant_id: tenantId
      }
    });

    if (response.error) {
      logError("Error inviting user", response.error);
      throw new Error(await genFunctionErrorMessage({ error: response.error, fallbackMessage: INVITE_USER_ERROR }));
    }
  } catch (error) {
    logError("Error in inviteUser", error);
    throw error;
  }
};

/**
 * Delete a user from the current tenant by calling the delete-user edge function
 */
export const deleteUser = async (userId: string, tenantId: string): Promise<void> => {
  try {
    const response = await supabase.functions.invoke("delete-user", {
      body: {
        user_id: userId,
        tenant_id: tenantId
      }
    });

    if (response.error) {
      logError("Error deleting user", response.error);
      throw new Error(await genFunctionErrorMessage({ error: response.error, fallbackMessage: DELETE_USER_ERROR }));
    }
  } catch (error) {
    logError("Error in deleteUser", error);
    throw error;
  }
};

/**
 * Update user's analysis groups
 */
export const updateUserAnalysisGroups = async (
  userId: string,
  analysisGroups: string[] | null
): Promise<void> => {
  try {
    const { error } = await supabase
      .from("account_user")
      .update({ analysis_groups: analysisGroups })
      .eq("id", userId);

    if (error) {
      logError("Error updating user analysis groups", error);
      throw error;
    }
  } catch (error) {
    logError("Error in updateUserAnalysisGroups", error);
    throw error;
  }
};
